import { ApiError } from "../api/http_client.js";
import { AuthSessionStore } from "../api/auth_session.js";
import {
  completeLevel,
  doubleLevelReward,
  purchaseItem,
  reportLevelFailed,
  reportLevelStart,
  rewardBooster,
  rewardHeart,
} from "../api/requests/economy.requests.js";
import { consumeBooster, grantTutorialBooster, purchaseBooster } from "../api/requests/cumulative_boosters.requests.js";
import { getPlayer } from "../api/requests/player.requests.js";
import type { PurchaseItem, RewardBoosterType } from "../api/dto/player.dto.js";
import type { CumulativeBoosterID } from "../cumulative_boosters/cumulative_booster_id.js";
import { adoptEconomyFromPlayer, syncProfileFromServer } from "../api/player_sync.js";
import { NetworkManager } from "../network_manager.js";
import { StatisticsStorage } from "../storage/statistics_storage.js";
import { LevelPackService } from "../level_pack/level_pack_service.js";
import { ConfigSyncService } from "../config_sync/config_sync_service.js";
import { isNetworkishError } from "../utils/network_errors.js";
import { OfflineJournal, isAtMostOnceAction, type OfflineAction } from "./offline_journal.js";
import { ServerClock } from "./server_clock.js";
import { crashCapture } from "../crash/crash_reporter.js";

export interface ReplayStats
{
  replayed: number;
  dropped: number;
  stopped: boolean;
}

type SyncListener = ( phase: "start" | "done", stats: ReplayStats | null ) => void;

function occurredAt( action: OfflineAction ): string | undefined
{
  const ms = Number( action.created_at );
  return Number.isFinite( ms ) && ms > 0 ? new Date( ms + ServerClock.getOffsetMs() ).toISOString() : undefined;
}

async function dispatch( action: OfflineAction ): Promise<void>
{
  const p = action.payload;
  switch ( action.type )
  {
    case "level_start":
      await reportLevelStart( Number( p.level ), typeof p.app_run_id === "string" ? p.app_run_id : undefined, occurredAt( action ) );
      return;
    case "level_complete":
      await completeLevel( Number( p.level ), Number( p.stars ) || 3 );
      return;
    case "level_failed":
      await reportLevelFailed( occurredAt( action ) );
      return;
    case "level_double_reward":
      await doubleLevelReward( Number( p.level ), Number( p.stars ) || 3 );
      return;
    case "purchase":
      await purchaseItem( p.item as PurchaseItem, {
        ...( typeof p.purchase_index === "number" ? { purchase_index: p.purchase_index } : {} ),
        price: Number( p.price ),
        qty: Number( p.qty ),
      } );
      return;
    case "booster_consume":
      await consumeBooster( p.booster_id as CumulativeBoosterID );
      return;
    case "booster_purchase":
      await purchaseBooster( p.booster_id as CumulativeBoosterID, Number( p.offer_id ) );
      return;
    case "reward_heart":
      await rewardHeart();
      return;
    case "reward_booster":
      await rewardBooster( p.booster_type as RewardBoosterType, String( p.placement ), String( p.ad_id ) );
      return;
    case "booster_tutorial":
      await grantTutorialBooster( p.booster_id as CumulativeBoosterID );
      return;
  }
}

const MAX_REPLAY_PASSES = 5;

export class OfflineSyncService
{
  private static syncing: Promise<void> | null = null;
  private static replaying: Promise<ReplayStats> | null = null;
  private static rerun_requested = false;
  private static listeners = new Set<SyncListener>();
  private static last_stats: ReplayStats | null = null;

  public static subscribe( listener: SyncListener ): () => void
  {
    this.listeners.add( listener );
    return () => this.listeners.delete( listener );
  }

  public static isSyncing(): boolean
  {
    return this.syncing !== null;
  }

  public static isReplaying(): boolean
  {
    return this.replaying !== null;
  }

  public static hasPending(): boolean
  {
    return OfflineJournal.hasPending();
  }

  public static getLastStats(): ReplayStats | null
  {
    return this.last_stats;
  }

  public static sync( reason: string ): Promise<void>
  {
    if ( this.syncing )
    {
      this.rerun_requested = true;
      return this.syncing;
    }

    this.syncing = ( async () =>
    {
      try
      {
        do
        {
          this.rerun_requested = false;
          await this.syncOnce( reason );
        }
        while ( this.rerun_requested && NetworkManager.isOnline() );
      }
      finally
      {
        this.syncing = null;
      }
    } )();

    return this.syncing;
  }

  private static async syncOnce( reason: string ): Promise<void>
  {
    if ( !NetworkManager.isOnline() || !AuthSessionStore.isLoggedIn() )
      return;

    const pending_before = OfflineJournal.count();
    if ( pending_before === 0 )
      return;

    console.info( `[OfflineSync] ${reason}: replaying ${pending_before} action(s)` );
    this.emit( "start", null );

    let stats = await this.replayPending();
    for ( let pass = 1; !stats.stopped && OfflineJournal.hasPending() && pass < MAX_REPLAY_PASSES; pass++ )
    {
      const more = await this.replayPending();
      stats = { replayed: stats.replayed + more.replayed, dropped: stats.dropped + more.dropped, stopped: more.stopped };
    }
    this.last_stats = stats;
    console.info( `[OfflineSync] replayed ${stats.replayed}, dropped ${stats.dropped}${stats.stopped ? ", stopped on network error" : ""}` );

    if ( !stats.stopped && OfflineJournal.hasPending() )
    {
      this.rerun_requested = true;
      this.emit( "done", stats );
      return;
    }

    if ( !stats.stopped )
      await this.refreshAfterSync();

    this.emit( "done", stats );
  }

  public static replayPending(): Promise<ReplayStats>
  {
    if ( this.replaying ) return this.replaying;
    this.replaying = this.replayPass().finally( () => { this.replaying = null; } );
    return this.replaying;
  }

  private static async replayPass(): Promise<ReplayStats>
  {
    const stats: ReplayStats = { replayed: 0, dropped: 0, stopped: false };

    const current_player = AuthSessionStore.getPlayerId();
    for ( const action of [ ...OfflineJournal.list() ] )
    {
      if ( action.player_id != null && current_player != null && action.player_id !== current_player )
      {
        console.warn( `[OfflineSync] dropping ${action.type} journaled by player ${action.player_id}, now logged in as ${current_player}` );
        OfflineJournal.remove( action.id );
        stats.dropped++;
        continue;
      }

      if ( action.state === "inflight" && isAtMostOnceAction( action.type ) )
      {
        console.warn( `[OfflineSync] ${action.type} was in flight when the app died — assuming applied` );
        OfflineJournal.remove( action.id );
        stats.dropped++;
        continue;
      }

      OfflineJournal.markInflight( action.id );
      try
      {
        await dispatch( action );
        OfflineJournal.remove( action.id );
        stats.replayed++;
      }
      catch ( e )
      {
        if ( e instanceof ApiError )
        {
          console.warn( `[OfflineSync] dropped ${action.type}:`, `${e.status} ${e.message}`, action.payload );
          OfflineJournal.remove( action.id );
          stats.dropped++;
          continue;
        }

        if ( isNetworkishError( e ) )
        {
          OfflineJournal.markPending( action.id );
          NetworkManager.handleNetworkError( e );
          stats.stopped = true;
          break;
        }

        console.error( `[OfflineSync] unexpected failure on ${action.type} — dropping it`, e, action.payload );
        crashCapture( e, { phase: "offline_replay", action_type: action.type } );
        OfflineJournal.remove( action.id );
        stats.dropped++;
      }
    }

    return stats;
  }

  public static async refreshAfterSync(): Promise<void>
  {
    try
    {
      const player = await getPlayer();
      syncProfileFromServer( player );
      adoptEconomyFromPlayer( player );
    }
    catch ( e )
    {
      console.warn( "[OfflineSync] player refresh failed", e );
    }

    await ConfigSyncService.refreshStale( "offline_sync" )
      .catch( e => console.warn( "[OfflineSync] event refresh failed", e ) );

    await OfflineSyncService.warmOfflineContent();
  }

  public static async warmOfflineContent(): Promise<void>
  {
    try
    {
      await LevelPackService.refresh();
      await LevelPackService.prefetchOfflineWindow( StatisticsStorage.load().level );
    }
    catch ( e )
    {
      console.warn( "[OfflineSync] level prefetch failed", e );
    }
  }

  private static emit( phase: "start" | "done", stats: ReplayStats | null ): void
  {
    for ( const listener of this.listeners )
    {
      try { listener( phase, stats ); } catch ( e ) { console.warn( "[OfflineSync] listener failed", e ); }
    }
  }
}
